import { useStore } from '../store/useStore'
import { X, Plus, FileCode, FileText, Palette, ChevronLeft, ChevronRight, Settings2 } from 'lucide-react'
import { useRef, useState, useEffect } from 'react'
import EditorSettings from './EditorSettings'

export default function FileTabs() {
  const { files, currentFile, setCurrentFile, addFile, deleteFile, isSaved } = useStore()
  const [showEditorSettings, setShowEditorSettings] = useState(false)
  const scrollContainerRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const getFileIcon = (fileName) => {
    if (fileName.endsWith('.js')) return <FileCode size={14} />
    if (fileName.endsWith('.html')) return <FileText size={14} />
    if (fileName.endsWith('.css')) return <Palette size={14} />
    return <FileCode size={14} />
  }

  const handleDeleteFile = (fileName, e) => {
    e.stopPropagation()
    if (files.length === 1) {
      alert('Cannot delete the last file')
      return
    }
    if (confirm(`Delete ${fileName}?`)) {
      deleteFile(fileName)
    }
  }

  const handleAddFile = () => {
    const fileName = prompt('Enter file name:', 'new-file.js')
    if (fileName?.trim()) {
      let newFileName = fileName.trim()
      // Add extension if missing
      if (!newFileName.includes('.')) {
        newFileName += '.js'
      }
      addFile(newFileName)
    }
  }

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current
    if (!container) return

    setShowLeftArrow(container.scrollLeft > 0)
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    )
  }

  const scroll = (direction) => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 200
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    checkScrollPosition()
    container.addEventListener('scroll', checkScrollPosition)
    window.addEventListener('resize', checkScrollPosition)

    return () => {
      container.removeEventListener('scroll', checkScrollPosition)
      window.removeEventListener('resize', checkScrollPosition)
    }
  }, [files])

  return (
    <div className="relative flex items-center bg-[#2d2d2d] border-b border-[#3e3e3e]">
      {/* Left arrow */}
      {showLeftArrow && (
        <button
          type="button"
          onClick={() => scroll('left')}
          className="absolute left-0 z-10 flex items-center justify-center w-8 h-full bg-gradient-to-r from-[#2d2d2d] to-transparent hover:from-[#3d3d3d] text-gray-400 hover:text-white transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* Scrollable tabs container */}
      <div
        ref={scrollContainerRef}
        className="flex items-center gap-1 px-2 py-1 overflow-x-hidden flex-1"
      >
      {files.map((file) => (
        <div
          key={file.name}
          onClick={() => setCurrentFile(file.name)}
          className={`group flex items-center gap-2 px-3 py-1.5 rounded-t cursor-pointer border-b-2 transition-colors min-w-fit ${
            currentFile === file.name
              ? 'bg-[#1e1e1e] border-p5-pink text-white'
              : 'bg-[#252526] border-transparent text-gray-400 hover:bg-[#2a2a2b] hover:text-white'
          }`}
        >
          {getFileIcon(file.name)}
          <span className="text-xs font-medium">{file.name}</span>
          {!isSaved && currentFile === file.name && (
            <span className="text-yellow-400 text-xs">●</span>
          )}
          {files.length > 1 && (
            <button
              type="button"
              onClick={(e) => handleDeleteFile(file.name, e)}
              className="opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white rounded p-0.5 transition-all"
              aria-label={`Close ${file.name}`}
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}
      
        {/* Add file button */}
        <button
          type="button"
          onClick={handleAddFile}
          className="flex items-center gap-1 px-2 py-1.5 text-gray-400 hover:text-white hover:bg-[#2a2a2b] rounded transition-colors"
          title="Add file"
          aria-label="Add file"
        >
          <Plus size={14} />
        </button>
        
        {/* Editor settings button */}
        <button
          type="button"
          onClick={() => setShowEditorSettings(true)}
          className="flex items-center gap-1 px-2 py-1.5 text-gray-400 hover:text-white hover:bg-[#2a2a2b] rounded transition-colors ml-auto"
          title="Editor Settings"
          aria-label="Editor Settings"
        >
          <Settings2 size={14} />
        </button>
      </div>
      
      {/* Editor Settings Modal */}
      <EditorSettings 
        isOpen={showEditorSettings} 
        onClose={() => setShowEditorSettings(false)} 
      />

      {/* Right arrow */}
      {showRightArrow && (
        <button
          type="button"
          onClick={() => scroll('right')}
          className="absolute right-0 z-10 flex items-center justify-center w-8 h-full bg-gradient-to-l from-[#2d2d2d] to-transparent hover:from-[#3d3d3d] text-gray-400 hover:text-white transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  )
}
